/* eslint-disable react-hooks/exhaustive-deps */
import * as yup from 'yup'
import {
  Box,
  Button,
  FormControlLabel,
  Radio,
  RadioGroup,
  TextField,
  Typography
} from '@mui/material'
import StyledAccordion from 'components/atoms/container/Accordion'
import BoxWithBorder from 'components/atoms/container/BoxWithBorder'
import RowStack from 'components/atoms/container/RowStack'
import FormDatePicker from 'components/atoms/input/FormDatePicker'
import FormInputBox from 'components/atoms/input/FormInputBox'
import UserConnectionSelect from 'components/atoms/input/UserConnectionSelect'
import RichTextContentEditor from 'components/molecules/journey/RichTextEditor'
import { useFormik } from 'formik'
import moment from 'moment'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import colors from 'styles/colors'
import { validationsRules } from 'utils/formValidations'

const BodyEditor = ({ onChange = () => {}, initialValue, editable }) => {
  const { t } = useTranslation()
  const [editing, setEditing] = useState(false)
  const [data, setData] = useState(initialValue)

  const handleEditButtonClick = () => {
    if (editing) {
      onChange(data)
    }
    setEditing(!editing)
  }
  return (
    <>
      <RowStack justifyContent='space-between' paddingY={3}>
        <Typography>{t('company.emailBody')}</Typography>
        {editable && (
          <Button onClick={handleEditButtonClick} size='small' color='primary'>
            {editing ? 'Save' : 'Edit'}
          </Button>
        )}
      </RowStack>
      {editable && editing ? (
        <RichTextContentEditor
          data={initialValue}
          changeCallback={updated => {
            setData(updated)
          }}
          emailEditor={true}
          inviteEmail={false}
          onFocusOut={() => {
            if (editing) {
              handleEditButtonClick()
            }
          }}
        />
      ) : (
        <BoxWithBorder
          paddingX={4}
          borderColor={colors.Grey100}
          borderWidth={1}>
          <Box
            width='100%'
            dangerouslySetInnerHTML={{
              __html: initialValue
            }}
          />
        </BoxWithBorder>
      )}
    </>
  )
}

const FacilitationRow = ({
  formik,
  milestoneStep,
  minDate,
  maxDate,
  index,
  preFacilitationEmails
}) => {
  const { t } = useTranslation()
  const existingFacilitation = milestoneStep.existingFacilitation
  const defaultTimelineEmail = Object.freeze(
    milestoneStep.defaultTimeline?.preFacilitationEmail
  )
  const hasDefaultEmail = Boolean(
    milestoneStep.defaultTimeline?.preFacilitationEmail
  )
  const [hasFacilitation, setHasFacilitation] = useState(
    Boolean(existingFacilitation)
  )
  const [showForm, setShowForm] = useState(false)
  const [defaultEmail, setDefaultEmail] = useState(
    Boolean(
      existingFacilitation
        ? !existingFacilitation.goalTimeline?.cohortSessionId
        : hasDefaultEmail
    )
      ? '1'
      : '0'
  )

  const facilitationFormik = useFormik({
    initialValues: {
      facilitationDate: existingFacilitation
        ? moment.utc(existingFacilitation.facilitationDate)
        : null,
      userId: existingFacilitation ? existingFacilitation.userId : null,
      subject: existingFacilitation
        ? existingFacilitation.goalTimeline.preFacilitationEmail?.subject
        : defaultTimelineEmail?.subject,
      body: existingFacilitation
        ? existingFacilitation.goalTimeline.preFacilitationEmail?.body
        : defaultTimelineEmail?.body
    },
    enableReinitialize: true,
    validateOnBlur: true,
    validationSchema: yup.object({
      facilitationDate: validationsRules.dateRequired
    }),
    onSubmit: values => {
      if (!values.facilitationDate) {
        return
      }
      const updatingFacilitations = [...formik.values.facilitations]
      const currentFacilitation = updatingFacilitations?.find(
        f =>
          f.goalTimelineId === milestoneStep.defaultTimeline?.id ||
          f.endGoalStepId === milestoneStep.endGoalStepId
      )

      const updatingValues = {
        userId: values.userId,
        facilitationDate: values.facilitationDate,
        goalTimelineId:
          defaultEmail === '1'
            ? milestoneStep.defaultTimeline?.id
            : existingFacilitation?.goalTimelineId,
        endGoalStepId: milestoneStep.endGoalStepId,
        id: existingFacilitation?.id,
        deleting: !hasFacilitation
      }
      // Manage existing facilitation update and delete
      if (existingFacilitation) {
        if (!hasFacilitation) {
          updatingValues.deleting = true
        } else {
          updatingValues.deleting = false
          updatingValues.updating = true
        }
      }
      // Manage customized timeline item create/update
      const validEmail = values.subject?.length && values.body?.length
      const preFacilitationEmail = {
        subject: values.subject,
        body: values.body
      }
      if (
        !existingFacilitation &&
        milestoneStep.defaultTimeline &&
        (defaultEmail === '1' || !preFacilitationEmails)
      ) {
        // Use default timeline
        updatingValues.goalTimelineId = milestoneStep.defaultTimeline.id
      } else if (
        !existingFacilitation?.goalTimeline?.cohortSessionId &&
        ((preFacilitationEmails && defaultEmail === '0') ||
          !milestoneStep.defaultTimeline)
      ) {
        // Create new customized timeline
        updatingValues.goalTimeline = {
          endGoalStepId: milestoneStep.endGoalStepId,
          goalId: milestoneStep.goalId,
          number: index + 1
        }
        if (validEmail) {
          updatingValues.goalTimeline.preFacilitationEmail =
            preFacilitationEmail
        }
      } else if (existingFacilitation?.goalTimeline?.cohortSessionId) {
        // If change from customized to default, delete customized timeline
        if (defaultEmail === '1' || !hasFacilitation) {
          updatingValues.goalTimeline = {
            ...existingFacilitation.goalTimeline,
            deleting: true
          }
        } else {
          // Update customized timeline
          updatingValues.goalTimeline = {
            ...existingFacilitation.goalTimeline,
            deleting: false,
            preFacilitationEmail: validEmail
              ? preFacilitationEmail
              : existingFacilitation.goalTimeline.preFacilitationEmail,
            updating: true
          }
        }
      }

      // Update to parent formik
      if (currentFacilitation) {
        Object.keys(updatingValues).forEach(key => {
          currentFacilitation[key] = updatingValues[key]
        })
      } else {
        updatingFacilitations.push({
          ...updatingValues
        })
      }
      formik.setFieldValue('facilitations', updatingFacilitations, false)
    }
  })

  useEffect(() => {
    if (existingFacilitation) {
      facilitationFormik.handleSubmit()
    }
  }, [])

  const disableEdit =
    existingFacilitation &&
    moment.utc(existingFacilitation.facilitationDate) < moment()

  const disableEmailEdit = defaultEmail === '1' || disableEdit

  useEffect(() => {
    if (facilitationFormik.dirty) {
      facilitationFormik.submitForm()
    }
  }, [facilitationFormik.values, defaultEmail])
  const handleDelete = () => {
    setShowForm(false)
    setHasFacilitation(false)
    facilitationFormik.submitForm()
  }

  const handleExpand = () => {
    if (hasFacilitation) {
      setShowForm(!showForm)
    } else {
      facilitationFormik.resetForm()
      setHasFacilitation(true)
      setShowForm(true)
      const submitted = formik.values.facilitations.find(
        f => f.endGoalStepId === milestoneStep.endGoalStepId
      )
      if (submitted) {
        facilitationFormik.submitForm()
      }
    }
  }

  const handleDefaultEmail = e => {
    setDefaultEmail(e.target.value)
    facilitationFormik.submitForm()
  }
  return (
    <StyledAccordion
      initialExpanded={false}
      expanded={showForm}
      onChange={handleExpand}
      summaryComponent={
        <RowStack justifyContent='space-between' spacing={2}>
          <Typography variant='h6'>{`${index + 1}. ${milestoneStep.name}`}</Typography>
          {!hasFacilitation && (
            <Typography variant='body1Small' noWrap={true} paddingRight={4}>
              {t('company.addSessionHere')}
            </Typography>
          )}
        </RowStack>
      }
      summaryProps={{
        sx: {
          backgroundColor: hasFacilitation
            ? colors.SecondaryLightGreen
            : colors.MetalGrey50,
          borderRadius: 4,
          paddingLeft: 4
        }
      }}
      showExpandIcon={hasFacilitation}
      sx={{ ':before': { display: 'none' } }}>
      <RowStack justifyContent='space-between'>
        <Typography>{t('company.facilitationDate')}</Typography>
        {!disableEdit && (
          <Button
            variant='text'
            size='small'
            color='error'
            sx={{ textDecoration: 'underline' }}
            onClick={handleDelete}>
            {t('company.deleteSession')}
          </Button>
        )}
      </RowStack>
      <FormDatePicker
        minDate={minDate}
        maxDate={maxDate}
        attributeName={'facilitationDate'}
        formik={facilitationFormik}
        disablePast
        disabled={disableEdit}
        sx={[
          {
            '& .MuiInputBase-root': {
              minWidth: 0
            }
          }
        ]}
        required
      />
      <Typography>{t('company.facilitator')}</Typography>
      <UserConnectionSelect
        formik={facilitationFormik}
        fieldName='userId'
        placeholder={t('company.selectFacilitator')}
        useAdminData={true}
        coaches={true}
        disabled={disableEdit}
      />
      {preFacilitationEmails && (
        <>
          <RowStack>
            <Typography>
              {t('company.preFacilitationReminderEmails')}
            </Typography>
            <RadioGroup row value={defaultEmail} onChange={handleDefaultEmail}>
              <FormControlLabel
                value='1'
                control={<Radio />}
                label={t('company.default')}
                disabled={!hasDefaultEmail || disableEdit}
              />
              <FormControlLabel
                value='0'
                control={<Radio />}
                label={t('company.customized')}
                disabled={disableEdit}
              />
            </RadioGroup>
          </RowStack>
          <Typography>{t('company.emailSubject')}</Typography>
          {defaultEmail === '1' ? (
            <TextField
              value={defaultTimelineEmail?.subject}
              disabled
              sx={{ width: '100%' }}
            />
          ) : (
            <FormInputBox
              formik={facilitationFormik}
              attributeName='subject'
              sx={{ width: '100%' }}
              disabled={disableEmailEdit}
            />
          )}
          {defaultEmail === '1' ? (
            <BodyEditor
              initialValue={defaultTimelineEmail?.body}
              editable={false}
            />
          ) : (
            <BodyEditor
              initialValue={facilitationFormik.values.body}
              onChange={value =>
                facilitationFormik.setFieldValue('body', value)
              }
              editable={!disableEmailEdit}
            />
          )}
        </>
      )}
    </StyledAccordion>
  )
}

export default FacilitationRow
